import { Vue } from 'vue-property-decorator';
import APP_CONST from '@/constants/AppConst';
import { ICoreIds } from '@/Model/model';
import handlerStatesCities from 'countrycitystatejson';
import statesCities from 'countries-states-cities';
import { ScreenText } from '@/lang/ScreenText';
import { DayAbbreviation, Day } from '@/Model/sessions/types';
import { ApiErrorResponse, ApiErrorType } from '@/Model/error/api/types';
import { ErrorMessages } from '@/lang/errorText';
import UIkit from 'uikit';
import ToastType from '@/Model/toastTypes';
import { PaymentStatus } from '@/Model/enrollment/types';
import { SessionBillingCycle } from '@/Model/sessions/catalogTypes';
import { getInstance } from '@/auth';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import { GlobalModule } from '@/store/global/globalModule';
const globalModule = getModule(GlobalModule, store);
import { getDecodedToken } from '@/services/common/httpClient';
import { LDClient, LDContext } from 'launchdarkly-js-client-sdk';
import { SegmentProps } from '@/Model/segment/types';



function commonCoreidsData(userId: any, userRoles: ICoreIds[]) {
  let programId: number = 0,
    siteId: number = 0,
    roleId: number = 0,
    accountId: number = 0;

  userRoles &&
    userRoles.length &&
    userRoles.forEach((item: any) => {
      /* eslint-disable no-prototype-builtins */
      if (item.hasOwnProperty('programId')) {
        programId = item.programId;
      }
      if (item.hasOwnProperty('accountId')) {
        accountId = item.accountId;
      }
      if (item.hasOwnProperty('siteId')) {
        siteId = item.siteId;
      }
      if (item.hasOwnProperty('roleId')) {
        roleId = item.roleId || 0;
      }
      /* eslint-enable no-prototype-builtins */
    });
  userId = parseInt(userId);
  return { id: userId, programId, siteId, roleId, accountId };
}

export default class APP_UTILITIES extends Vue {
  public static readonly currencyFmt = new Intl.NumberFormat(
    APP_CONST.APP_LOCALE,
    {
      style: 'currency',
      currency: APP_CONST.CURRENCY,
    }
  );

  static get accountSlug(): string {
    try {
      const authService = getInstance();
      const accountDesignCookie =
        JSON.parse(String(APP_UTILITIES.getCookie('accountDesign'))) ||
        authService.accountDesign;
      return accountDesignCookie.slug || location.pathname.split('/')[1];
    }
    catch (error) {
      return localStorage.getItem('slug') || location.pathname.split('/')[1];
    }
  }

  public static getCookie(name: string) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) == 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  }

  /**
   * Set a new context to be used for evaluating feature flags
   * @param newContext - the new context to be used
   * @returns LDContext - the new context
   */
  public static async setLDContext(newContext: LDContext): Promise<LDContext> {
    return globalModule.storedLDClient.identify(newContext);
  }

  /**
   * Set a new Launch Darkly client to be used for all launch darkly actions
   * @param client - the new LDClient to be used
   */
  public static async setLDClient(client: LDClient): Promise<void> {
    return globalModule.mutateLDClient(client);
  }

  /**
   * Gets the current Launch Darkly client
   * @returns LDClient - the current Launch Darkly  lient
   */
  public static async getLDClient(): Promise<LDClient> {
    return globalModule.storedLDClient;
  }

  /**
   * Gets the current Launch Darkly context
   * @returns LDContext - the current Launch Darkly context
   */
  public static async getLDContext(): Promise<LDContext> {
    return globalModule.storedLDClient.getContext();
  }

  /**
   * Get the value of a feature flag
   * @param flag The key of the flag to evaluate
   * @returns boolean - the value of the flag
   */
  public static async getFeatureFlag(flag: string): Promise<boolean> {
    return globalModule.storedLDClient.variation(flag);
  }

  /**
   * Gets all feature flags for the current context
   */
  public static async getAllFeatureFlags(): Promise<any> {
    return globalModule.storedLDClient.allFlags();
  }

  /**
   * Waits for the Launch Darkly client to be initialized
   * @returns boolean - whether the Launch Darkly client has been initialized
   */
  public static async waitForLDClient(): Promise<void> {
    return globalModule.storedLDClient.waitForInitialization();
  }

  /**
   *
   * @param status The payment status number sent back from the API
   * @returns A text value of the payment status.
   */
  public static getScreenTextForPaymentStatus(status: PaymentStatus): string {
    const screenText = new ScreenText();
    return screenText.getScreenText(`PAYMENT_STATUS_${status.toString()}`);
  }

  public static setCookie(name: string, value: string, days: number) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(
        date.getTime() +
          days
            * APP_CONST.HOUR
            * APP_CONST.MINUTES
            * APP_CONST.SECOND
            * APP_CONST.MILISECOND
      );
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie =
      name +
      '=' +
      (value || '') +
      expires +
      `;path=/${APP_UTILITIES.accountSlug}/;`;
  }

  public static removeCookies() {
    const res = document.cookie;
    const multiple = res.split(';');

    for (let i = 0; i < multiple.length; i++) {
      const key = multiple[i].split('=');
      // Added logic to keep cookies related to auth0 and stripe to avoid any issues with the libraries
      const shouldKeep = key[0].includes('auth0') || key[0].includes('stripe');

      if (!shouldKeep) {
        document.cookie = `${key[0]}=; expires=${new Date()}; path=/${
          APP_UTILITIES.accountSlug
        }/;`;
      }
    }

    return true;
  }

  public static showToastMessage(message: string, status: ToastType) {
    let icon = '';
    switch (status) {
      case ToastType.Success:
        icon = "<i class='fa-solid fa-circle-check'></i>";
        break;
      case ToastType.Error:
        icon = "<i class='fa-solid fa-circle-exclamation'></i>";
        break;
      default:
        icon = '';
    }
    const messageWithIcon = `<div class='toast-wrapper'>${icon} ${message}</div>`;
    UIkit.notification.closeAll();
    UIkit.notification({
      message: messageWithIcon,
      status: status,
      pos: 'top-center',
      timeout: 5000,
    });
  }

  public static capitalize(s: string) {
    if (typeof s !== 'string') {
      return '';
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  public static checkBrowserStatus() {
    return navigator.onLine;
  }

  public static hasOnlyAlphabetDotSpace(myString: any) {
    let isValid = false;
    const regex = /^[\u00AA-\u017Ea-zA-Z. '-]*$/;
    isValid = regex.test(myString);
    return isValid;
  }

  public static getFullDate(date: any) {
    const dateObj = new Date(date);
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    const output = month + '/' + day + '/' + year;
    return output;
  }

  public static getDaysAbbreviations(
    short: boolean = false
  ): Array<DayAbbreviation> {
    const screenText = new ScreenText();
    const dayKeys: Array<string> = Object.keys(Day).filter((k) =>
      isNaN(Number(k))
    ); // Filter out the numeric keys
    const daysAbbreviations: Array<DayAbbreviation> = dayKeys.map((day) => ({
      day: Day[day as keyof typeof Day],
      abbreviation: screenText.getScreenText(
        `${day}_ABBR${short
          ? '_SHORT'
          : ''}`
      ),
    }));
    return daysAbbreviations;
  }

  // Legacy core ids implementation, used in stores for getting the coresids synchronously
  public static coreidsSync() {
    const userDt: string | null =
      APP_UTILITIES.getCookie('user_role') ||
      sessionStorage.getItem('user_role');
    const userRoles: ICoreIds[] = userDt && JSON.parse(userDt);
    return commonCoreidsData(APP_UTILITIES.getCookie('user_id'), userRoles);
  }

  // Updated coredids to use auth0 token information to fetch data on vue lifecycles,etc.. asynchronously
  public static async  coreidsAsync() {

    const { userId, userRoles: roles }: any = await getDecodedToken();

    const userDt: string | null =
      APP_UTILITIES.getCookie('user_role') ||
      sessionStorage.getItem('user_role') ||
      JSON.stringify(roles);
    const userRoles: ICoreIds[] = userDt && JSON.parse(userDt);
    return commonCoreidsData(userId, userRoles);
  }

  static async roleBasedNav() {
    const { roleId } = await APP_UTILITIES.coreidsAsync();
    const decideNav = JSON.parse(JSON.stringify(APP_CONST.NAV_DECIDER));
    const navView =
      (decideNav.length &&
        decideNav.find((item: any) => item.roleType == roleId)) ||
      [];
    return navView;
  }

  public static checkMobile() {
    let check = false;
    // eslint-disable-next-line no-useless-escape
    (function(a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      ) {
        check = true;
      }
    })(navigator.userAgent || navigator.vendor);
    return check;
  }

  public static isMobile() {
    if (document.documentElement.clientWidth < 769) {
      return true;
    }
    else if (document.documentElement.clientWidth >= 769) {
      return false;
    }
    return false;
  }

  public static formatCorrectDate(date: string) {
    const dateObj = new Date(date);
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj
      .getFullYear()
      .toString()
      .substr(2, 2);
    const h = this.checkTime(dateObj.getHours());
    const m = this.checkTime(dateObj.getMinutes());
    const output = month + '/' + day + '/' + year + ' ' + h + ':' + m;
    return output;
  }

  public static checkTime(i: any) {
    if (i < 10) {
      i = '0' + i;
    }
    return i;
  }

  public static formatShortDateFullYear(date: string) {
    const dateObj = new Date(date);
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear().toString();
    const output = month + '/' + day + '/' + year;
    return output;
  }

  // format date in mm/dd/yy
  public static formatDate(date: string) {
    const monthNames = [
      '01',
      '02',
      '03',
      '04',
      '05',
      '06',
      '07',
      '08',
      '09',
      '10',
      '11',
      '12',
    ];
    const dateObj = new Date(date);
    const month = monthNames[dateObj.getMonth()];
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    const output = month + '/' + day + '/' + year.toString().substr(-2);
    return output;
  }

  public static removeSpecificCookie(cookieName: string) {
    document.cookie =
      cookieName +
      `=; expires=${new Date()}; path=/${APP_UTILITIES.accountSlug}/;`;
  }

  public static formatAMPM(date: string) {
    let hours: any = new Date(date).getHours();
    let minutes: any = new Date(date).getMinutes();
    const ampm: string = hours >= 12
      ? 'PM'
      : 'AM';
    hours = hours % 12;
    hours = hours
      ? hours
      : 12;
    minutes = minutes < 10
      ? '0' + minutes
      : minutes;
    const strTime =
      hours + (minutes > APP_CONST.ZERO
        ? ':' + minutes
        : '') + ampm;
    return strTime;
  }

  public static formatAMPM00(date: string) {
    let hours: any = new Date(date).getHours();
    let minutes: any = new Date(date).getMinutes();
    const ampm: string = hours >= 12
      ? 'PM'
      : 'AM';
    hours = hours % 12;
    hours = hours
      ? hours
      : 12;
    minutes = minutes.toString().padStart(2, '0');
    const strTime = hours + ':' + minutes + ' ' + ampm;
    return strTime;
  }

  public static fetchStates(countryName: string) {
    return Object.keys(
      handlerStatesCities.getCountryByShort(countryName).states
    ).filter((response: any) => {
      if (response != 'AP' && response != 'AA' && response != 'AE') {
        return response;
      }
    });
  }

  public static sortAscending(list: any, sortOn: string) {
    list &&
      list.sort((a: any, b: any) => {
        if (a[sortOn] < b[sortOn]) {
          return APP_CONST.MINUSONE;
        }
        if (a[sortOn] > b[sortOn]) {
          return APP_CONST.ONE;
        }
        return APP_CONST.ZERO;
      });
    return list;
  }

  public static removeStates(states: any, statesToRemove: any) {
    states = states.filter(
      (item: any) => !statesToRemove.includes(item.state_code)
    );
    return states;
  }

  public static getStatesShort(countryID: number) {
    return Object.values(statesCities.getStatesOfCountry(countryID));
  }

  public static getStatesShortFinalList(countryID: number) {
    let states: any[] = APP_UTILITIES.getStatesShort(countryID);
    const removedStates: any = APP_CONST.REMOVED_STATES;
    states = APP_UTILITIES.removeStates(states, removedStates);
    states = APP_UTILITIES.sortAscending(states, 'state_code');
    states = states.map((state: any) => {
      return state.state_code;
    });
    return states;
  }

  public static formatNumberdash(phoneNumberString: any) {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return +match[1] + '-' + match[2] + '-' + match[3];
    }
  }

  public static removeSpecialCharacters(val: string) {
    if (!val) {
      return '';
    }
    const cleaned: any =
      val && val.charAt(0) == '0'
        ? ''
        : ('' + val).replace(/\D/g, '').toString();
    return cleaned;
  }

  public static formatFullYearDate(date: string) {
    const dateObj = new Date(date);
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear().toString();
    const output = year + '-' + month + '-' + day;
    return output;
  }

  public static formatFullDateForElastic(date: string) {
    const dateObj = new Date(date);
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear().toString();
    const output = `${month} - ${day} - ${year}`;
    return output;
  }

  public static formatPhoneNumberWithBracket(phoneNumberString: any) {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    /* istanbul ignore else */
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    else {
      return phoneNumberString;
    }
  }

  public static convert12hrFormateTimeFromDate(date: string) {
    const datetime = new Date(date);
    const finalDate = datetime.getTime();
    return new Date(finalDate)
      .toLocaleTimeString(APP_CONST.APP_LOCALE, {
        hour: 'numeric',
        hour12: true,
        minute: 'numeric',
      })
      .toLowerCase();
  }

  public static formatLocaleDateTime(datetime: string | Date, options?: any) {
    return new Date(datetime).toLocaleString(APP_CONST.APP_LOCALE, options);
  }

  public static errorToApiErrorResponse(error: unknown): ApiErrorResponse {
    const isKnownApiErrorResponse = APP_UTILITIES.isApiErrorResponse(error);
    if (isKnownApiErrorResponse) {
      const errorAny = error as ApiErrorResponse | { data: ApiErrorResponse };
      return ('data' in errorAny
        ? errorAny.data
        : errorAny) as ApiErrorResponse;
    }
    const errorMessage = APP_UTILITIES.getErrorMessage(error);
    return {
      name: ApiErrorType.Error_Unknown,
      value: errorMessage,
      resourceNotFound: false,
      searchedLocation: '',
    };
  }

  /**
   * Attempts to return the `message` from an `Error` object. If an object is
   * not an instance of `Error`, attempts to pull the value from one of the following props:
   * `error.message`, `error.data.value`, `error.data.Message`. If none of these
   * are found, returns the generic `Common_Error` message from
   * `@/lang/errorText/ErrorMessages.getErrorMessages('Common_Error')` instead.
   * @param error The object to attempt to retrieve the error message from.
   * @returns The error message string.
   */
  public static getErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    let errorMessage = ((new ErrorMessages().getErrorMessages(
      'Common_Error'
    ) as unknown) as { title: string }).title; // Cast to unknown because it actually returns an object, not a string like the function definition says
    if (error instanceof Error) {
      errorMessage = error.message;
    }
    else if (error === Object(error)) {
      // To account for other existing patterns in the app
      // Ideally should be an Error object though
      try {
        const errorAny = error as any;
        const keys = Object.keys(errorAny);
        if (keys.includes('message') && errorAny.message) {
          errorMessage = errorAny.message;
        }
        else if (keys.includes('data')) {
          if (typeof errorAny.data === 'string') {
            errorMessage = errorAny.data;
          }
          else if (errorAny.data.value) {
            errorMessage = errorAny.data.value;
          }
          else if (errorAny.data.Message) {
            errorMessage = errorAny.data.Message;
          }
        }
      }
      catch (e) {
        console.error(
          'Unhandled error type for getErrorMessage',
          `'${typeof error}'`,
          e
        );
      }
    }
    return errorMessage;
  }

  /**
   * Checks whether the passed `error` object is an instance of `ApiErrorResponse`.
   * @param error The object to check.
   * @returns Returns `true` if the object is an instance of `ApiErrorResponse`, otherwise `false`.
   */
  public static isApiErrorResponse(error: unknown): boolean {
    let isKnownApiErrorResponse = false;
    if (error === Object(error)) {
      try {
        const errorAny = error as any;
        const toTest = errorAny && errorAny.data
          ? errorAny.data
          : errorAny;
        if (toTest) {
          const knownObj: ApiErrorResponse = {
            name: ApiErrorType.Error_Unknown,
            value: '',
            resourceNotFound: false,
            searchedLocation: '',
          };
          const keysTypes = Object.entries(toTest).map((kt) => ({
            key: kt[0],
            type: typeof kt[1],
          }));
          const knownKeysTypes = Object.entries(knownObj).map((kt) => ({
            key: kt[0],
            type: typeof kt[1],
          }));
          if (keysTypes.length === knownKeysTypes.length) {
            const sameKeysAndTypes = keysTypes.every(
              (kt) =>
                !!knownKeysTypes.find(
                  (kkt) => kkt.key === kt.key && kkt.type === kt.type
                )
            );
            if (sameKeysAndTypes) {
              isKnownApiErrorResponse = true;
            }
          }
        }
      }
      catch (e) {
        console.error(`Unhandled error checking ${error}`, e);
      }
    }
    return isKnownApiErrorResponse;
  }

  public static calculateAge(
    dob: string | undefined,
    ageAsOf: string | undefined
  ) {
    if (dob === undefined || ageAsOf === undefined) {
      return;
    }

    const dobDate = this.deconstructedDOB(dob);
    const ageAsOfDate = this.deconstructedDOB(ageAsOf);

    if (dobDate === undefined || ageAsOfDate === undefined) {
      return;
    }

    let age = ageAsOfDate.getUTCFullYear() - dobDate.getUTCFullYear();
    const monthDiff = ageAsOfDate.getUTCMonth() - dobDate.getUTCMonth();
    const dayDiff = ageAsOfDate.getUTCDate() - dobDate.getUTCDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }

    return age;
  }

  public static deconstructedDOB(dobStr: string | undefined) {
    if (dobStr === undefined) {
      return;
    }

    const dob = dobStr.split(' ')[0];

    return new Date(dob);
  }

  public static billingEnumToTextAbreviation(
    billingCycle: SessionBillingCycle
  ) {
    switch (billingCycle) {
      case 1:
        return 'wk';
      case 2:
        return 'mo';
    }
  }

  /**
   * Takes the given DateTime string and returns the date portion in the format `Month D, YYYY` where the `Month` is the short month abbreviation followed by a `.` if applicable.
   * @param datetime The DateTime string to parse
   * @returns The date portion of the passed `datetime` in the format `Month D, YYYY` (`Month` short abbreviated if applicable).
   */
  public static formatLongDateFromDateTime(dateTime: string): string {
    let monthAbbr = APP_UTILITIES.formatLocaleDateTime(dateTime, {
      month: 'short',
    });
    if (
      monthAbbr
      != APP_UTILITIES.formatLocaleDateTime(dateTime, {
        month: 'long',
      })
    ) {
      monthAbbr += '.';
    }
    const day = APP_UTILITIES.formatLocaleDateTime(dateTime, {
      day: 'numeric',
    });
    const year = APP_UTILITIES.formatLocaleDateTime(dateTime, {
      year: 'numeric',
    });
    return `${monthAbbr} ${day}, ${year}`;
  }

  /**
   * Takes the given DateTime string and returns the time portion in the format `HH:MM XM` with the option to truncate if the minutes are `:00`.
   * @param dateTime The DateTime string to parse
   * @param truncateZeroMins Whether or not a minute value of `:00` should be truncated (omitted) from the result. Defaults to `false`.
   * @returns The time portion of the passed `datetime` in the format `HH:MM XM`
   */
  public static format12HrTimeFromDateTime(
    dateTime: string,
    truncateZeroMins = false
  ): string {
    const dt = new Date(dateTime);
    return APP_UTILITIES.formatLocaleDateTime(dateTime, {
      hour12: true,
      hour: 'numeric',
      minute: dt.getMinutes() || !truncateZeroMins
        ? 'numeric'
        : undefined,
    });
  }

  public static parseStringToRegExp(
    regExpString: string
  ):RegExp {
    // Remove the leading and trailing forward slashes (/)
    // Added for current implementation , currently regex is stored with trailing slashes
    const pattern = regExpString.slice(1, -1);
    // Create the regex object using the pattern
    const regExpParsed = new RegExp(pattern);

    return regExpParsed;
  }

  /**
 * Generates a random alphanumeric ID.
 * @param {number} length - The length of the generated ID.
 * @returns {string} The generated alphanumeric ID.
 */
  public static generateRandomAlphanumericId(length: number): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const charactersLength = characters.length;

    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result;
  }

  // Segment tracking function, defined based on the segment types
  // wrapper functions were created since the segment types are  not available in the current project setup
  public static segmentTrackEvent(event: SegmentProps['event'], properties?: SegmentProps['properties'], options?: SegmentProps['options'], callback?: SegmentProps['callback']) {
    (window as any).analytics.track(event,properties,options,callback);
  }

}
